import { WarningAmberRounded } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

export interface DataDisplayProps {
  /** 현재 값. null/undefined = 앱 미실행 (데이터 없음) */
  value: number | null | undefined;
  /** 전일/이전 값. 주어지면 하락률 계산해서 급락 시 ⚠️ 표시 */
  previousValue?: number | null;
  /** 하락 경고 발동 임계값 (의뢰서 기본 10%) */
  dropThresholdPct?: number;
  /** 단위 접미사 (예: '점', '%') */
  suffix?: string;
}

/**
 * 의뢰서 4-3 데이터 표시 원칙.
 * - 미실행 (null/undefined): — (회색)
 * - 실행 후 0점: 0 (빨강 강조)
 * - 정상 점수: 기본 색
 * - 전일 대비 임계값 이상 하락: 숫자 + ⚠️ -N%
 */
export function DataDisplay({
  value,
  previousValue,
  dropThresholdPct = 10,
  suffix = '',
}: DataDisplayProps) {
  if (value === null || value === undefined) {
    return (
      <Typography component="span" color="text.disabled" data-testid="data-display-missing">
        —
      </Typography>
    );
  }

  if (value === 0) {
    return (
      <Typography
        component="span"
        color="error.main"
        fontWeight={600}
        data-testid="data-display-zero"
      >
        0{suffix}
      </Typography>
    );
  }

  const dropPct = computeDropPct(value, previousValue, dropThresholdPct);

  if (dropPct !== null) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        component="span"
        data-testid="data-display-drop"
      >
        <Typography component="span">
          {value}
          {suffix}
        </Typography>
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          component="span"
          sx={{ color: 'warning.main' }}
        >
          <WarningAmberRounded sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600} component="span">
            {dropPct}%
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Typography component="span" data-testid="data-display-normal">
      {value}
      {suffix}
    </Typography>
  );
}

function computeDropPct(
  current: number,
  previous: number | null | undefined,
  threshold: number,
): number | null {
  if (typeof previous !== 'number' || previous <= 0) return null;
  const delta = ((current - previous) / previous) * 100;
  if (delta > -threshold) return null;
  return Math.round(delta);
}
